var _excluded = ["inputRef", "leadingZero", "max", "min", "name", "onChange", "placeholder", "required", "reverse", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAnimation } from 'framer-motion';
import useMediaQuery from '@mui/material/useMediaQuery';
import Autocomplete from './Autocomplete';
import Select from './Select';
import { StyledWrapper, StyledTextField } from './DateNumberInput.styled';
var DateNumberInput = function DateNumberInput(_ref) {
  var inputRef = _ref.inputRef,
    leadingZero = _ref.leadingZero,
    max = _ref.max,
    min = _ref.min,
    name = _ref.name,
    onChange = _ref.onChange,
    placeholder = _ref.placeholder,
    required = _ref.required,
    reverse = _ref.reverse,
    value = _ref.value,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useState = useState(false),
    error = _useState[0],
    setError = _useState[1];
  var _useState2 = useState(null),
    selected = _useState2[0],
    setSelected = _useState2[1];
  // const classes = useStyles({error, required, value});
  var controls = useAnimation();
  var maxLength = max.toString().length;
  var isNotMobile = useMediaQuery(function (theme) {
    return theme.breakpoints.up('sm');
  });
  useEffect(function () {
    if (error) {
      controls.start({
        translateX: ['-12px', '10px', '-6px', '4px'],
        transition: {
          duration: 0.25,
          ease: 'easeInOut'
        }
      });
      inputRef.current.focus();
      if (isNotMobile) {
        inputRef.current.select();
      }
      setTimeout(function () {
        return setError(false);
      }, 500);
    }
  }, [error, controls, inputRef, isNotMobile]);
  useEffect(function () {
    if (value && parseInt(value, 10) > max) {
      onChange(max);
      setError(true);
    }
  }, [value, max, onChange]);
  var handleKeyDown = function handleKeyDown(e) {
    // prevent none [0-9] characters that are allowed by number inputs
    var notAllowed = ['-', '+', '.', 'e', 'E'];
    if (notAllowed.includes(e.key)) {
      e.preventDefault();
    }
  };
  var handleChange = function handleChange(newValue) {
    var hasError = false;
    var updatedValue = newValue;
    // strip leading zeroes...
    updatedValue = parseInt(updatedValue, 10).toString();

    // prevent user entering more characters that allowed
    if (updatedValue.length > maxLength) {
      updatedValue = updatedValue.substring(0, maxLength);
      hasError = true;
    }

    // prevent the user from entering a number larger than the max
    if (parseInt(updatedValue, 10) > max) {
      updatedValue = max;
      hasError = true;
    }
    if (hasError) {
      setError(true);
    }
    if (leadingZero && updatedValue !== '0') {
      while (updatedValue.length < maxLength) {
        updatedValue = "0" + updatedValue;
      }
    }
    onChange(updatedValue);
  };
  var handleAutocompleteChange = function handleAutocompleteChange(event, option) {
    setSelected(option);
    handleChange(option.value);
  };
  var handleNativeChange = function handleNativeChange(_ref2) {
    var target = _ref2.target;
    onChange(target.value);
  };
  var options = useMemo(function () {
    var optionArray = [];
    var count = min;
    while (count < max + 1) {
      var label = count.toString();
      if (leadingZero) {
        while (label.length < maxLength) {
          label = "0" + label;
        }
      }
      optionArray.push({
        label: label,
        value: isNotMobile ? count : parseInt(label, 10)
      });
      count += 1;
    }
    return reverse ? optionArray.reverse() : optionArray;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max, leadingZero, maxLength]);
  var autoCompleteDefault = Object.values(options).filter(function (option) {
    return option.value === value;
  })[0];
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    animate: controls,
    error: error
  }, isNotMobile ? /*#__PURE__*/React.createElement(Autocomplete, _extends({
    fullWidth: true,
    autoHighlight: true,
    autoSelect: true,
    blurOnSelect: true,
    value: selected || autoCompleteDefault || null,
    noOptionsText: "Invalid",
    options: [{
      value: '',
      label: ''
    }].concat(options),
    disableClearable: true,
    name: name,
    required: required,
    onChange: handleAutocompleteChange,
    getOptionLabel: function getOptionLabel(option) {
      return option.label ? option.label : '';
    },
    renderInput: function renderInput(params) {
      return /*#__PURE__*/React.createElement(StyledTextField, _extends({}, params, {
        value: value,
        placeholder: placeholder,
        name: name,
        variant: "outlined",
        onChange: handleNativeChange,
        onKeyDown: handleKeyDown,
        inputRef: inputRef,
        required: required,
        inputProps: _extends({
          autoComplete: 'off'
        }, params.inputProps)
      }));
    }
  }, rest)) : /*#__PURE__*/React.createElement(Select, _extends({
    fullWidth: true,
    value: !isNaN(value) ? value : null,
    required: required,
    placeholder: placeholder,
    onChange: handleNativeChange,
    options: [{
      value: '',
      label: placeholder
    }].concat(options),
    inputRef: inputRef,
    name: name,
    native: true
  }, rest)));
};
DateNumberInput.propTypes = process.env.NODE_ENV !== "production" ? {
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]).isRequired,
  leadingZero: PropTypes.bool,
  max: PropTypes.number.isRequired,
  min: PropTypes.number,
  name: PropTypes.string.isRequired,
  reverse: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
} : {};
DateNumberInput.defaultProps = {
  leadingZero: true,
  min: 0,
  placeholder: '',
  required: false,
  reverse: false,
  value: ''
};
export default DateNumberInput;